/**
 * StoryRail (web) — the curated Stories on the trips page: a "Be inspired" header above a horizontal rail
 * of portrait cover cards (the standard {@link ImageCard}), each linking to the full Story page. Pulls
 * the live stories from the shared `StoriesState`; hidden while they load and when there are none.
 *
 * Scrolls rather than wraps, via the shared `CarouselRow` (the same affordance the article's card rows and
 * the chat image gallery use, arrows and edge fade included). A grid would reflow to a second line as the
 * catalog grows, and the rail is meant to read as one row you scroll along — which is also how the mobile
 * home rail behaves.
 */
import { ROUTES } from '@elara/client-core/constants';

import { useStoriesState } from '#states/hooks.js';
import { CarouselRow } from '#ui/CarouselRow.js';
import { ImageCard } from '#views/ImageCard.js';
import { storyImageUrl } from '#views/stories/story-assets.js';

/** The rail card's width in CSS pixels (`w-60`), for picking its cover's stored width. */
const RAIL_CARD_WIDTH = 240;

export function StoryRail() {
  const stories = useStoriesState();
  if (stories.stories.length === 0) return null;

  return (
    <section className="w-full max-w-7xl mx-auto flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-display">Be inspired</h2>
        <p className="text-sm text-muted-foreground">Handpicked by the Elara team</p>
      </div>
      <CarouselRow label="stories" contentClassName="pb-2 gap-6 snap-x" fadeFrom="from-background-secondary">
        {stories.stories.map((story) => (
          <div key={story.id} className="w-60 shrink-0 snap-start">
            <ImageCard
              title={story.title}
              image={storyImageUrl(story.coverImage, RAIL_CARD_WIDTH)}
              orientation="portrait"
              to={ROUTES.STORY}
              params={{ storyId: story.id }}
            />
          </div>
        ))}
      </CarouselRow>
    </section>
  );
}
